import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { topicsList, commentsList } from "@/mocks/topics";

const FORM_URL = "https://readdy.ai/api/form/db7jgcomkl3spn8sm9bg";

const rules = [
  "友善交流，尊重每一位粉丝的观点与感受。",
  "请围绕大岛优香的作品、动态展开讨论，勿发布无关或广告内容。",
  "禁止人身攻击、恶意引战及任何违规信息。",
  "所有话题与留言均为站内演示内容，不代表明星本人立场。",
];

export default function FanInteractionPage() {
  usePageMeta({
    title: "大岛优香粉丝互动 - 话题讨论与留言板",
    description:
      "大岛优香粉丝互动专区，浏览最新热门话题、参与讨论并查看粉丝留言，和同好一起分享对作品的喜爱与感受。",
    keywords: "大岛优香粉丝互动,话题讨论,粉丝留言,应援",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的分享，话题已提交，我们会尽快展示给大家！");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) params.append(key, str);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.detail || parsed?.meta?.message || parsed?.message || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("感谢你的分享，话题已提交，我们会尽快展示给大家！");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : `提交失败：${serverMsg || "请稍后再试"}`
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800">粉丝互动</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
          粉丝互动
        </h1>
        <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
          这里是属于大家的讨论空间。浏览热门话题、留下你的观点，也欢迎发起一个新话题，
          和更多喜欢大岛优香的朋友一起聊聊。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
        {/* Left: topics + comments */}
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-chat-heart-line text-primary-500 w-5 h-5 flex items-center justify-center" />
              互动话题
            </h2>
            <div className="space-y-3">
              {topicsList.map((topic) => (
                <div
                  key={topic.id}
                  className="bg-background-100 border border-background-200 rounded-lg p-5 hover:border-primary-300 transition-colors"
                >
                  <h3 className="font-heading font-bold text-foreground-950 leading-snug mb-3">
                    {topic.title}
                  </h3>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                      {topic.author}
                    </span>
                    <span className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                        {topic.createdAt}
                      </span>
                      <span className="flex items-center gap-1 text-primary-600 font-medium">
                        <i className="ri-chat-3-line w-4 h-4 flex items-center justify-center" />
                        {topic.commentCount} 条留言
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-message-2-line text-secondary-600 w-5 h-5 flex items-center justify-center" />
              最新留言
            </h2>
            <div className="space-y-4">
              {commentsList.map((c) => (
                <div key={c.id} className="flex gap-4">
                  <span className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-heading font-bold shrink-0">
                    {c.user.slice(0, 1)}
                  </span>
                  <div className="flex-1 bg-background-100 border border-background-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground-900">{c.user}</span>
                      <span className="text-xs text-foreground-400">{c.date}</span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right: form + rules */}
        <aside className="space-y-8">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              发起新话题
            </h2>
            <form data-readdy-form id="fan-topic-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="topic_title"
                  className="block text-sm text-foreground-700 mb-1.5"
                >
                  话题标题
                </label>
                <input
                  id="topic_title"
                  name="topic_title"
                  type="text"
                  required
                  maxLength={60}
                  placeholder="用一句话概括你的话题"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="content" className="block text-sm text-foreground-700 mb-1.5">
                  话题内容
                </label>
                <textarea
                  id="content"
                  name="content"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="说说你想和大家聊的内容（500 字以内）"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-3 py-2 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                />
              </div>

              {/* anti-spam honeypot */}
              <input
                type="text"
                name="website_alt"
                className="hp-field-guard"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="w-full px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                提交话题
              </button>

              {status !== "idle" && (
                <p
                  className={`text-sm rounded-md px-3 py-2 animate-fade-up ${
                    status === "success"
                      ? "bg-primary-100 text-primary-800"
                      : "bg-accent-100 text-accent-900"
                  }`}
                >
                  {feedback}
                </p>
              )}
            </form>
            <p className="text-xs text-foreground-500 mt-3">
              提交后我们会尽快审核并展示，感谢你的参与。
            </p>
          </div>

          <div className="bg-background-100 border border-background-200 rounded-lg p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-accent-600 w-5 h-5 flex items-center justify-center" />
              互动规则
            </h2>
            <ul className="space-y-3">
              {rules.map((rule) => (
                <li key={rule} className="flex gap-2 text-sm text-foreground-700 leading-relaxed">
                  <i className="ri-checkbox-circle-line text-primary-500 w-4 h-4 flex items-center justify-center mt-0.5 shrink-0" />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}